import { useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import type { LeaveSaveOutcome, SaveBeforeLeaveResult } from "../core/saveBeforeLeaveOutcome";
import { resolveEditFormUnsavedChangesCopy } from "../core/copy";
import { useFormikSaveBeforeLeave } from "./useFormikSaveBeforeLeave";
import { shouldEnableFormikSaveBeforeLeave, shouldGuardFormikCancel } from "./formikGuardPolicy";

export type FormikUnsavedGuardConfigInput = {
    readOnlyForm?: boolean;
    enableUnsavedChangesGuard?: boolean;
    isNewRecord?: boolean;
    newRecordLabel?: string;
    unsavedChangesTitle?: string;
    unsavedChangesMessage?: string;
    unsavedChangesCancelLabel?: string;
    unsavedChangesContinueWithoutSavingLabel?: string;
    enableSaveBeforeLeave?: boolean;
    leaveAfterSaveRef?: React.MutableRefObject<boolean>;
    saveOutcomeRef?: React.MutableRefObject<LeaveSaveOutcome>;
    onSaveBeforeLeave?: () => Promise<boolean | SaveBeforeLeaveResult>;
};

export type FormikUnsavedGuardConfig = {
    isDirty: boolean;
    onSave?: () => Promise<boolean | SaveBeforeLeaveResult>;
    cancelLabel: string;
    title?: string;
    message?: string;
    continueWithoutSavingLabel?: string;
};

/** Shared Formik guard wiring for provider and local footer modes. */
export function useFormikUnsavedGuardConfig({
    readOnlyForm = false,
    enableUnsavedChangesGuard = true,
    isNewRecord = false,
    newRecordLabel,
    unsavedChangesTitle,
    unsavedChangesMessage,
    unsavedChangesCancelLabel,
    unsavedChangesContinueWithoutSavingLabel,
    enableSaveBeforeLeave = false,
    leaveAfterSaveRef,
    saveOutcomeRef,
    onSaveBeforeLeave: onSaveBeforeLeaveProp,
}: FormikUnsavedGuardConfigInput): FormikUnsavedGuardConfig {
    const { dirty, submitForm, validateForm, setTouched } = useFormikContext();

    const saveBeforeLeaveEnabled = shouldEnableFormikSaveBeforeLeave(enableSaveBeforeLeave, isNewRecord);

    const dummyLeaveAfterSaveRef = useRef(false);
    const dummySaveOutcomeRef = useRef<LeaveSaveOutcome>(null);

    const onSaveBeforeLeaveFromRefs = useFormikSaveBeforeLeave({
        submitForm,
        validateForm,
        setTouched,
        leaveAfterSaveRef: leaveAfterSaveRef ?? dummyLeaveAfterSaveRef,
        saveOutcomeRef: saveOutcomeRef ?? dummySaveOutcomeRef,
    });

    const onSaveBeforeLeave =
        saveBeforeLeaveEnabled && leaveAfterSaveRef && saveOutcomeRef
            ? onSaveBeforeLeaveProp ?? onSaveBeforeLeaveFromRefs
            : onSaveBeforeLeaveProp;

    const unsavedChangesCopy = useMemo(
        () =>
            resolveEditFormUnsavedChangesCopy({
                isNewRecord,
                newRecordLabel,
                title: unsavedChangesTitle,
                message: unsavedChangesMessage,
                cancelLabel: unsavedChangesCancelLabel,
                continueWithoutSavingLabel: unsavedChangesContinueWithoutSavingLabel,
            }),
        [
            isNewRecord,
            newRecordLabel,
            unsavedChangesTitle,
            unsavedChangesMessage,
            unsavedChangesCancelLabel,
            unsavedChangesContinueWithoutSavingLabel,
        ]
    );

    const isDirty = shouldGuardFormikCancel(enableUnsavedChangesGuard, dirty, readOnlyForm);

    return {
        isDirty,
        onSave: saveBeforeLeaveEnabled ? onSaveBeforeLeave : undefined,
        cancelLabel: unsavedChangesCopy.unsavedChangesCancelLabel ?? "Stay",
        title: unsavedChangesCopy.unsavedChangesTitle,
        message: unsavedChangesCopy.unsavedChangesMessage,
        continueWithoutSavingLabel: unsavedChangesCopy.unsavedChangesContinueWithoutSavingLabel,
    };
}
